import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, NavSidebarLink, Skeleton } from 'src/common';
import { useTasks } from 'src/models/bids/BidFull/hooks';
import { BidQuestionType } from 'src/models/bids/BidFull/types';
import { generateTaskUrl, useTaskNav } from 'src/models/bids/BidTask/hooks';
import { trackBTOpenDetailedTaskView } from 'src/segment/events';

interface GroupLinksProps {
  tasksType: BidQuestionType;
}

const GroupLinks: FC<GroupLinksProps> = ({ tasksType }) => {
  const { t } = useTranslation();
  const { data: navData } = useTaskNav();

  const allTasks = useTasks();
  const {
    data: state,
    offset,
    setOffset,
    groupId,
    limit
  } = tasksType === BidQuestionType.Manual ? allTasks.manual : allTasks.generated;
  const tasks = useMemo(() => (state?.data ? state.data.tasks.filter(task => !!task) : []), [state?.data]);
  const isMore = useMemo(() => !!state?.data && tasks.length < state.data.totalCount, [state?.data, tasks.length]);

  const onClick = useCallback(async () => {
    await state?.fetchMore({ variables: { offset: offset + limit } });
    setOffset(offset + limit);
  }, [state, offset, limit, setOffset]);

  const onClickTaskLink = useCallback(
    task => {
      trackBTOpenDetailedTaskView({
        taskId: task.id,
        groupId,
        bidId: task.bidId,
        taskType: task.taskType,
        eventSource: 'TaskPageNavigation'
      });
    },
    [groupId]
  );

  return state?.loading ? (
    <Skeleton loading active />
  ) : (
    <>
      {tasks &&
        !!tasks.length &&
        tasks.map(
          task =>
            task && (
              <NavSidebarLink
                to={generateTaskUrl(task.id) ?? ''}
                title={!!task.title ? task.title : t('BiddingTool.noTitle')}
                isActive={navData?.activeTaskId === task.id}
                isExpanded={true}
                key={task.id}
                onClick={() => onClickTaskLink(task)}
              />
            )
        )}
      {isMore && (
        <Button type="link" onClick={onClick} loading={state?.fetchingMore}>
          {t('Common.showMore')}
        </Button>
      )}
    </>
  );
};

export default GroupLinks;
